import { Authorisation } from "@iventis/domain-model/model/authorisation";
import { MenuItem, Select } from "@mui/material";
import React, { useState } from "react";
import { Content } from "@iventis/translations/content/typed-content";
import { useIventisTranslate } from "@iventis/translations/use-iventis-translate";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ClickAwayListenerToolTip, InfoTooltip } from "@iventis/components";
import { InteractiveElement, styled } from "@iventis/styles";
import { PermissionResourceType } from "@iventis/domain-model/model/permissionResourceType";

const selectableAuthLevels: Authorisation[] = [Authorisation.Admin, Authorisation.Write, Authorisation.Read, Authorisation.Deny];

export const AuthorisationSelector: React.FC<{
    authLevel: Authorisation;
    onChange: (value: Authorisation) => void;
    resourceType: PermissionResourceType;
    disabled?: boolean;
    open?: boolean;
    className?: string;
    dataTestId?: string;
}> = ({ authLevel, onChange, disabled, open, className, dataTestId, resourceType }) => {
    const translate = useIventisTranslate();
    const [openHelp, setOpenHelp] = useState(false);
    const translatedAuthLevels: [Authorisation, string, string][] = selectableAuthLevels.map((level) => {
        let descriptionKey: string;

        switch (resourceType) {
            case "Folder":
                descriptionKey = "descriptionFolder";
                break;
            case "Map":
                descriptionKey = "descriptionPlan";
                break;
            case "Layer":
                descriptionKey = "descriptionLayer";
                break;
            default:
                descriptionKey = null;
                break;
        }

        return [level, translate(Content.permissions.authLevels[level.toLowerCase()].title), translate(Content.permissions.authLevels[level.toLowerCase()][descriptionKey])];
    });

    const selectedAuthLevel = translatedAuthLevels.find((item) => item[0] === authLevel);
    const selectedLabel = selectedAuthLevel ? selectedAuthLevel[1] : "";
    const selectedDescription = selectedAuthLevel ? selectedAuthLevel[2] : "";

    const getKey = () => {
        if (open === true) {
            return "open";
        }
        if (open === false) {
            return "closed";
        }
        if (open === undefined) {
            return "default";
        }
        return "";
    };

    return (
        <StyledContainer>
            <StyledSelect
                className={className}
                disabled={disabled}
                open={open}
                key={getKey()}
                labelId="label"
                id="select"
                value={authLevel}
                variant="standard"
                onChange={(event) => onChange(event.target.value as Authorisation)}
                data-testid={dataTestId}
                renderValue={(value) => {
                    const permissionLabel = translatedAuthLevels.find((item) => item[0] === value);
                    return <span>{permissionLabel[1]}</span>;
                }}
            >
                {translatedAuthLevels.map(([level, label, description]) => (
                    <StyledMenuItem key={level} value={level} data-testid={`auth-level-${label}`}>
                        <p>{label}</p>
                        <StyledDescription>{description}</StyledDescription>
                    </StyledMenuItem>
                ))}
            </StyledSelect>
            {selectedDescription && (
                <ClickAwayListenerToolTip onClickAway={() => setOpenHelp(false)}>
                    <InfoTooltip title={selectedLabel} text={selectedDescription} open={openHelp} onClose={() => setOpenHelp(false)} placement="right">
                        <InteractiveElement onClick={() => setOpenHelp(!openHelp)}>
                            <FontAwesomeIcon icon={["far", "circle-info"]} />
                        </InteractiveElement>
                    </InfoTooltip>
                </ClickAwayListenerToolTip>
            )}
        </StyledContainer>
    );
};

const StyledContainer = styled.div`
    display: flex;
    gap: 1rem;
    align-items: center;
`;

const StyledSelect = styled(Select)`
    > div {
        display: flex;
        flex-direction: column;
    }

    .MuiInputBase-input.MuiSelect-select {
        height: unset;
    }
`;
const StyledMenuItem = styled(MenuItem)`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
`;
const StyledDescription = styled.p`
    font-size: smaller;
`;
